import { useCallback } from "react";
import { useProfile } from "@/context/ProfileContext";
import { useIdleLogoutMinutes } from "@/hooks/queries/use-idle-logout-setting";
import { useIdleLogout } from "@/hooks/use-idle-logout";
import { performIdleLogout } from "@/lib/idle-logout/perform-idle-logout";
import { IdleLogoutWarningDialog } from "./IdleLogoutWarningDialog";

/**
 * Mounts the GRA-80 idle-logout tracker for the signed-in session. Rendered
 * once from `AppLayout`, inside the authenticated tree, so it only runs when
 * there is a `businessProfile` to read the setting from.
 *
 * The setting is polled live (see `useIdleLogoutMinutes`) rather than read
 * once from `businessProfile`, so an admin flipping it takes effect for
 * every open tab without a reload — including turning it off, which tears
 * the tracker down immediately via `useIdleLogout`'s effect cleanup.
 */
export function IdleLogoutManager() {
  const { activeBusinessId } = useProfile();
  const idleLogoutMinutes = useIdleLogoutMinutes(activeBusinessId);

  const handleTimeout = useCallback(() => {
    // The tracker only ever fires while `idleLogoutMinutes` is a positive
    // number (see `useIdleLogout`'s guard), so this is always set here.
    void performIdleLogout(idleLogoutMinutes ?? 10);
  }, [idleLogoutMinutes]);

  const { status, remainingMs, extend } = useIdleLogout({
    timeoutMinutes: idleLogoutMinutes,
    onTimeout: handleTimeout,
  });

  return (
    <IdleLogoutWarningDialog
      open={status === "warning"}
      remainingMs={remainingMs}
      onContinue={extend}
    />
  );
}
